import { useMemo, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { PhoneInput, formatUSPhone, isValidUSPhone } from '../../components/ui/PhoneInput';
import { useUpdateClient, useUpdateClientStatus } from '../../hooks/useClients';
import { useCanSetClientInactive } from '../../hooks/useCanSetClientInactive';
import { useAuthStore } from '../../stores/authStore';
import { getLocations } from '../../services/api/locationService';
import {
  ClientForm,
  ClientFieldRow,
  ClientFormError,
  Textarea,
  FieldLabel,
  FieldGroup,
} from './ClientFormLayout';
import { GENDER_OPTIONS, clientStatusOptions } from './clientFormConstants';
import type { Client, ClientStatus, UpdateClientDto } from '../../types/clients';

interface EditClientFormValues {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  phone: string;
  email: string;
  externalId: string;
  notes: string;
  primaryCenterId: string;
  status: string;
}

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  /** Raise when opened from inside another overlay so this stacks above it. */
  zIndex?: number;
}

export const EditClientModal = ({ isOpen, onClose, client, zIndex }: EditClientModalProps) => {
  const { hasPermission } = useAuthStore();
  const canUpdate = hasPermission('clients:manage');
  const updateClient = useUpdateClient();
  const updateStatus = useUpdateClientStatus();
  // Same option set as the bulk Update Status dialog and the drawer menu (ADO #584).
  const statusOptions = clientStatusOptions(useCanSetClientInactive());

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<EditClientFormValues>();

  useEffect(() => {
    if (client) {
      reset({
        firstName: client.firstName,
        lastName: client.lastName,
        dateOfBirth: client.dateOfBirth ? client.dateOfBirth.slice(0, 10) : '',
        gender: client.gender ?? '',
        phone: formatUSPhone(client.phone ?? ''),
        email: client.email ?? '',
        externalId: client.externalId ?? '',
        notes: client.notes ?? '',
        primaryCenterId: client.primaryCenterId ?? '',
        status: client.status,
      });
    }
  }, [client, reset]);

  const { data: locationsData } = useQuery({
    queryKey: ['locations', 'all-for-clients'],
    queryFn: () => getLocations({ limit: 200 }),
    enabled: isOpen,
  });

  const centerOptions = useMemo(
    () => [
      { value: '', label: 'Select center...' },
      ...(locationsData?.data ?? []).map((loc) => ({ value: loc.id, label: loc.name })),
    ],
    [locationsData],
  );

  const onSubmit = (values: EditClientFormValues) => {
    if (!client || !canUpdate) return;
    const dto: UpdateClientDto = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      dateOfBirth: values.dateOfBirth || undefined,
      gender: values.gender || undefined,
      phone: values.phone.trim() || undefined,
      email: values.email.trim() || undefined,
      externalId: values.externalId.trim() || undefined,
      notes: values.notes.trim() || undefined,
      primaryCenterId: values.primaryCenterId || undefined,
    };
    // Status goes through the dedicated status endpoint, the same write the bulk dialog
    // and drawer use (ADO #584 / #586). `PATCH /clients/:id` refuses INACTIVE because
    // it lacks the org-wide-reach guard, and routing every status change one way
    // means Inactive set here behaves identically to the bulk path. Fields are saved
    // first: once a client goes off the roster, the field edit would 404.
    const nextStatus = values.status as ClientStatus;
    const statusChanged = nextStatus !== client.status;
    updateClient.mutate(
      { id: client.id, dto },
      {
        onSuccess: () => {
          if (!statusChanged) return handleClose();
          updateStatus.mutate({ id: client.id, status: nextStatus }, { onSuccess: handleClose });
        },
      },
    );
  };

  const handleClose = () => {
    updateClient.reset();
    updateStatus.reset();
    onClose();
  };

  const saveError = (updateClient.error ?? updateStatus.error) as any;
  const isSaving = updateClient.isPending || updateStatus.isPending;

  if (!client) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Client"
      size="md"
      zIndex={zIndex}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          {canUpdate && (
            <Button
              variant="primary-blue"
              onClick={handleSubmit(onSubmit)}
              isLoading={isSaving}
            >
              Save Changes
            </Button>
          )}
        </>
      }
    >
      <ClientForm onSubmit={handleSubmit(onSubmit)}>
        {saveError && (
          <ClientFormError>
            {saveError?.response?.data?.message ||
              'Failed to update client. Please try again.'}
          </ClientFormError>
        )}

        <ClientFieldRow>
          <Input
            {...register('firstName', { required: 'First name is required' })}
            label="First Name"
            placeholder="e.g. John"
            error={errors.firstName?.message}
            required
            fullWidth
          />
          <Input
            {...register('lastName', { required: 'Last name is required' })}
            label="Last Name"
            placeholder="e.g. Doe"
            error={errors.lastName?.message}
            required
            fullWidth
          />
        </ClientFieldRow>

        <ClientFieldRow>
          <Input {...register('dateOfBirth')} label="Date of Birth" type="date" fullWidth />
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <Select
                label="Gender"
                options={GENDER_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                fullWidth
              />
            )}
          />
        </ClientFieldRow>

        <ClientFieldRow>
          <Controller
            name="phone"
            control={control}
            rules={{
              validate: (v: string) =>
                isValidUSPhone(v) || 'Enter a valid 10-digit US phone number',
            }}
            render={({ field, fieldState }) => (
              <PhoneInput
                label="Phone"
                fullWidth
                value={field.value}
                error={fieldState.error?.message}
                onChange={field.onChange}
              />
            )}
          />
          <Input
            {...register('email')}
            label="Email"
            type="email"
            placeholder="e.g. client@example.com"
            fullWidth
          />
        </ClientFieldRow>

        <ClientFieldRow>
          <Input
            {...register('externalId')}
            label="External ID / MRN"
            placeholder="e.g. MRN-12345"
            fullWidth
          />
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                label="Status"
                options={statusOptions}
                value={field.value}
                onChange={field.onChange}
                fullWidth
              />
            )}
          />
        </ClientFieldRow>

        <ClientFieldRow>
          <Controller
            name="primaryCenterId"
            control={control}
            render={({ field }) => (
              <Select
                label="Primary Center"
                options={centerOptions}
                value={field.value}
                onChange={field.onChange}
                fullWidth
              />
            )}
          />
        </ClientFieldRow>

        <FieldGroup>
          <FieldLabel htmlFor="edit-client-notes">Notes</FieldLabel>
          <Textarea
            id="edit-client-notes"
            {...register('notes')}
            placeholder="Optional notes about this client..."
          />
        </FieldGroup>
      </ClientForm>
    </Modal>
  );
};
